import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'
import {
  outputApprovalSchema,
  type OutputApprovalDecision,
  type OutputApprovalRequest,
} from '@mindkeep/shared'
import { apiRequest } from '@/lib/api-client'

export const outputApprovalKeys = {
  all: ['output-approvals'] as const,
  list: () => [...outputApprovalKeys.all, 'list'] as const,
}

const approvalsResponseSchema = z.object({ approvals: z.array(outputApprovalSchema) })
const approvalResponseSchema = z.object({ approval: outputApprovalSchema })

export function useOutputApprovals() {
  return useQuery({
    queryKey: outputApprovalKeys.list(),
    queryFn: () => apiRequest({ path: '/outputs/approvals', schema: approvalsResponseSchema }),
    staleTime: 15_000,
  })
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function useRequestOutputReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OutputApprovalRequest) =>
      apiRequest({ method: 'POST', path: '/outputs/approval', body, schema: approvalResponseSchema }),
    onSuccess: () => {
      toast.success('Zur Review angemeldet.')
      void qc.invalidateQueries({ queryKey: outputApprovalKeys.all })
    },
    onError: (err) => toast.error(errorMessage(err, 'Review-Anmeldung fehlgeschlagen')),
  })
}

export function useDecideOutputApproval() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OutputApprovalDecision) =>
      apiRequest({ method: 'POST', path: '/outputs/approval/decide', body, schema: approvalResponseSchema }),
    onSuccess: (data) => {
      toast.success(
        data.approval.status === 'approved' ? 'Output freigegeben.' : 'Output abgelehnt.',
      )
      void qc.invalidateQueries({ queryKey: outputApprovalKeys.all })
    },
    onError: (err) => toast.error(errorMessage(err, 'Review-Entscheid fehlgeschlagen')),
  })
}
